import {Component, Property, MeshComponent} from '@wonderlandengine/api';

export class RemoteWeaponRigManager extends Component {
    static TypeName = 'remote-weapon-rig-manager';

    static Properties = {
        debug: Property.bool(true),
        localRigSource: Property.object(),
        player1: Property.object(),
        player2: Property.object(),
        player3: Property.object(),
        player4: Property.object(),
        player5: Property.object(),
        player6: Property.object(),
    };

    // Pre-allocate memory buffers to prevent Garbage Collection overhead
    _tempPos = new Float32Array(3);
    _tempRot = new Float32Array(4);
    _tempSca = new Float32Array(3);
    
    // Buffers for debug verification
    _verifyPos = new Float32Array(3);
    _verifyRot = new Float32Array(4);
    _verifySca = new Float32Array(3);

    start() {
        if (!this.localRigSource) {
            throw new Error(`${this.type}: localRigSource is missing!`);
        }

        this._remoteRigs = [
            this.player1, this.player2, this.player3, 
            this.player4, this.player5, this.player6
        ].filter(obj => obj !== null);

        this.instantiateRigs();
    }

    instantiateRigs() {
        for (let i = 0; i < this._remoteRigs.length; i++) {
            this._syncHierarchyRecursive(this.localRigSource, this._remoteRigs[i]);
        }
    }

    _syncHierarchyRecursive(sourceNode, remoteNode) {
        this._syncAndVerifyNode(sourceNode, remoteNode);

        const sourceChildren = sourceNode.children;
        const remoteChildren = remoteNode.children;

        // Optimization: Assuming exact hierarchy clones, bypass .find() and use index directly.
        // If hierarchies might differ, use a pre-built Map instead of .find()
        const len = Math.min(sourceChildren.length, remoteChildren.length);
        for (let i = 0; i < len; i++) {
            // Optional safety check: if (sourceChildren[i].name === remoteChildren[i].name)
            this._syncHierarchyRecursive(sourceChildren[i], remoteChildren[i]);
        }
    }

    _syncAndVerifyNode(source, remote) {
        // 1. Get Source Data into pre-allocated arrays (Zero Allocation)
        source.getPositionLocal(this._tempPos);
        source.getRotationLocal(this._tempRot);
        source.getScalingLocal(this._tempSca);

        // 2. Apply directly to Remote
        remote.setPositionLocal(this._tempPos);
        remote.setRotationLocal(this._tempRot);
        remote.setScalingLocal(this._tempSca);

        // 3. Verification Logic
        if (this.debug) {
            remote.getPositionLocal(this._verifyPos);
            remote.getRotationLocal(this._verifyRot);
            remote.getScalingLocal(this._verifySca);

            const posMatch = this._compareArrays(this._tempPos, this._verifyPos);
            const rotMatch = this._compareArrays(this._tempRot, this._verifyRot);
            const scaMatch = this._compareArrays(this._tempSca, this._verifySca);

            if (!posMatch || !rotMatch || !scaMatch) {
                console.warn(
                    `${this.type} [${remote.name}]: ❌ Parity fail\n` +
                    `  > Pos: ${posMatch ? 'OK' : 'FAIL'}\n` +
                    `  > Rot: ${rotMatch ? 'OK' : 'FAIL'}\n` +
                    `  > Sca: ${scaMatch ? 'OK' : 'FAIL'}`
                );
            }
        }

        // Note on Meshes: getComponents() allocates arrays. 
        // If this runs frequently, cache the MeshComponents during start() instead of querying them here.
        const sMeshes = source.getComponents(MeshComponent);
        if (sMeshes.length > 0) {
            const rMeshes = remote.getComponents(MeshComponent);
            for (let i = 0; i < rMeshes.length; i++) {
                rMeshes[i].active = sMeshes[i] ? sMeshes[i].active : true;
            }
        }
    }

    _compareArrays(a, b, epsilon = 0.0001) {
        // Optimized loop
        const len = a.length;
        for (let i = 0; i < len; i++) {
            if (Math.abs(a[i] - b[i]) > epsilon) return false;
        }
        return true;
    }
}